import http from '../../../Utils/http';
import responseMessageHandler from '../../../Utils/ResponseMessageHandler';
const DisplaysBulkUpload = {
    name: 'DisplaysBulkUpload',
    state: {
        data: {},
        loading: false,
        progress: 0,
        error: '',
        chunk: 0,
        uploadedIds: [],
    },
    reducers: {
        bulkUploadRequested(state) {
            return {
                ...state,
                loading: true,
                error: '',
            };
        },

        bulkUploadSucceeded(state, payload) {
            return {
                ...state,
                data: payload,
                loading: false,
                error: '',
            };
        },
        setUploadedIds(state, payload) {
            const ids = (payload && payload.length > 0) ? payload?.map(item => item?.id) : [];
            const merged = state.uploadedIds.concat(ids);
            return {
                ...state,
                uploadedIds: merged,
                chunk: state.chunk + 1,
            };

        },
        bulkUploadUpdated(state, payload, currentProgress) {
            return {
                ...state,
                data: payload,
                error: '',
                progress: currentProgress.toFixed(0),
            };
        },

        bulkUploadFailed(state, payload) {
            return {
                ...state,
                error: payload,
                loading: false,
                progress: 0,
                chunk: 0,
            };
        },
        clearStats(state) {
            return {
                ...state,
                loading: false,
                error: '',
                progress: 0,
                chunk: 0,
                uploadedIds: [],
            };
        },
    },
    effects: (dispatch) => ({
        async bulkUploadDisplays(data) {
            try {
                this.bulkUploadRequested();
                const idx = data.chunk;
                const displays = data.chunkData;
                const totalRecords = data.totalRecords;
                const score = getChunkUploadProgress(idx, Math.ceil(totalRecords / 40), totalRecords, 40);
                const previousScore = score.prevPercentage;
                const chunkScore = score.percentage - score.prevPercentage;
                const total = displays.length;
                let uploadedCount = 0;

                const updatedRows = await Promise.all(
                    displays.map(async (row) => {
                        let uploadedUrl = row.image;
                        if (uploadedUrl && uploadedUrl instanceof File) {
                            const result = await dispatch.CampaignStepsMangment.UploadNameImageToAws({ file: uploadedUrl, type: 'image' });
                            uploadedUrl = result;
                        }
                        uploadedCount++;
                        let imageProgress = ((uploadedCount / total) * ((chunkScore) * 0.8));
                        imageProgress = imageProgress + previousScore;
                        this.bulkUploadUpdated(null, imageProgress);
                        return {
                            ...row,
                            image: uploadedUrl,
                        };
                    })
                );
                const userInfo = JSON.parse(localStorage.getItem('userInfo'));
                const org_id = userInfo?.org_id;
                const payload = [];
                const simplifyAspectRatio = (width, height) => {
                    const w = Number(width);
                    const h = Number(height);
                    if (!Number.isFinite(w) || !Number.isFinite(h) || w <= 0 || h <= 0) return null;
                    const gcd = (a, b) => (b === 0 ? a : gcd(b, a % b));
                    const div = gcd(Math.trunc(w), Math.trunc(h));
                    return `${Math.trunc(w / div)}:${Math.trunc(h / div)}`;
                };

                for (const display of updatedRows) {
                    const visibility = display.visibility || 'Private';
                    const visibilityStatus = display?.rateScheduleData?.id ? 'available' : 'unavailable';
                    const allDisplayTypes = new Set();
                    
                    const screens = display.screens.map(screen => {
                        const types = Array.isArray(screen?.display_types) && screen.display_types.length > 0
                            ? screen.display_types
                            : (screen?.display_type ? [screen.display_type] : ['Zoned']);
                        
                        types.forEach((type) => {
                            if (type) {
                                const normalized = String(type).trim();
                                if (normalized === 'Full Screen') {
                                    allDisplayTypes.add('FULL_SCREEN');
                                } else if (normalized === 'Zoned') {
                                    allDisplayTypes.add('ZONED');
                                } else if (normalized === 'LED') {
                                    allDisplayTypes.add('LED');
                                } else {
                                    allDisplayTypes.add(normalized.toUpperCase().replace(/\s+/g, '_'));
                                }
                            }
                        });
                        
                        const displayType = types[0] || 'Zoned';
                        const ledWidth = displayType === 'LED' ? (Number(screen?.resolution_width) || null) : null;
                        const ledHeight = displayType === 'LED' ? (Number(screen?.resolution_height) || null) : null;
                        const ledAspect =
                            displayType === 'LED'
                                ? (screen?.led_aspect_ratio || simplifyAspectRatio(ledWidth, ledHeight))
                                : null;
                        const maxFileSizeMbRaw = Number(screen?.max_file_size_mb);
                        const maxFileSizeMb = displayType === 'LED'
                            ? (Number.isFinite(maxFileSizeMbRaw) ? Math.min(5, Math.max(1, Math.trunc(maxFileSizeMbRaw))) : 5)
                            : null;

                        return {
                            display_type: displayType,
                            orientation: displayType === 'Full Screen' ? 'Landscape' : (screen.orientations || 'Landscape'),
                            size: screen.size,
                            resolution: displayType === 'LED'
                                ? (ledWidth && ledHeight ? `${ledWidth}x${ledHeight}` : null)
                                : screen.resolutions,
                            led_width: ledWidth,
                            led_height: ledHeight,
                            led_aspect_ratio: ledAspect,
                            max_file_size_mb: maxFileSizeMb,
                            max_file_size: displayType === 'LED' ? maxFileSizeMb * 1024 * 1024 : null,
                            accepted_file_types:
                                displayType === 'LED'
                                    ? String(screen?.accepted_file_types || '')
                                        .split(',')
                                        .map((t) => t.trim())
                                        .filter(Boolean)
                                    : null,
                        };
                    });
                    
                    const displayTypeArray = Array.from(allDisplayTypes);
                    if (displayTypeArray.length === 0) {
                        displayTypeArray.push('ZONED');
                    }
                    
                    const generalInfo = {
                        address: display.location.location_address,
                        digital_signage_software: display.DSS,
                        display_location: display.displayLocation,
                        name: display.displayName,
                        media_player_sn: '',
                        org_id,
                        visibility,
                        visibilityStatus,
                        // region: display.region,
                        image: display.image,
                        location_id: display.location.id,
                        location: {
                            lat: display.location.latitude,
                            lng: display.location.longitude,
                        },
                        location_name: display.location.location_name,
                        numberOfScreens: String(display.screens.length),
                        rate_schedule_id: display?.rateScheduleData?.id || null,
                        rate_schedule_name: display?.rateScheduleData?.name || null,
                        display_type: displayTypeArray,
                    };
                    payload.push({ generalInfo, screens });
                }
                let uploadedRecordsProgress = 0;
                let lastProgress = (chunkScore * 0.8) + previousScore;
                const origin =
                    (typeof window !== 'undefined' && window?.location?.origin)
                        ? window.location.origin
                        : null;
                const requestBody = origin ? { origin, displays: payload } : payload;
                const response = await http.post('savvy/displays/bulk-upload', requestBody);
                if (response && response.data) {
                    uploadedRecordsProgress = lastProgress + chunkScore * 0.20;
                    if (uploadedRecordsProgress > lastProgress) {
                        lastProgress = uploadedRecordsProgress;
                        this.setUploadedIds(response.data.data);
                        this.bulkUploadUpdated(response.data, lastProgress);
                        dispatch({
                            type: 'helper/show',
                            payload: {
                                type: 'info',
                                message: `Uploading... ${uploadedRecordsProgress}%`,
                                show: true,
                            },
                        });
                    }
                } else {
                    throw new Error('No data received from the server');
                }

                this.bulkUploadSucceeded(response.data);
                dispatch({
                    type: 'helper/show',
                    payload: {
                        type: 'success',
                        message: 'Display bulk upload successful!',
                        show: true,
                    },
                });

            } catch (error) {
                this.bulkUploadFailed(error.message);
                dispatch({
                    type: 'helper/show',
                    payload: {
                        type: 'error',
                        message: responseMessageHandler.show(error.message),
                        show: true,
                    },
                });
            }
            function getChunkUploadProgress(currentChunkIndex, totalChunks, totalRecords, chunkSize) {
                if (totalChunks === 0 || totalRecords === 0 || chunkSize === 0) {
                    return { percentage: 0, prevPercentage: 0, recordsProcessed: 0, recordsRemaining: totalRecords };
                }

                const recordsProcessed = Math.min((currentChunkIndex + 1) * chunkSize, totalRecords);
                const recordsRemaining = Math.max(totalRecords - recordsProcessed, 0);
                const percentage = (recordsProcessed / totalRecords) * 100;

                const previousRecordsProcessed = Math.min(currentChunkIndex * chunkSize, totalRecords);
                const prevPercentage = (previousRecordsProcessed / totalRecords) * 100;


                return {
                    percentage: percentage,
                    prevPercentage: prevPercentage,
                    recordsProcessed,
                    recordsRemaining,
                };
            }

        }
    }),
    async ClearStats() {
        this.clearStats();
    }
};

export default DisplaysBulkUpload;
